import * as babylon from "@babylonjs/core";

import { BabylonState } from "../../state";

import {
  sampleAt,
  interpolationOf,
  type Interpolation,
} from "./setPositionsToTime";

/** A rotation cache: parallel arrays of sample times and orientations. */
export interface SampledRotations {
  logtimes: bigint[];
  allRotations: babylon.Quaternion[];
}

/**
 * The cached orientation at `time`: interpolated between the bracketing samples
 * when `interpolation` blends that pair (see `interpolatesBetween`), and
 * otherwise the latest sample at or before `time`.
 *
 * Slerp rather than lerp: lerping quaternion components takes a chord through
 * the unit sphere rather than an arc along it, which reads as a rotation that
 * slows in the middle and, past a quarter turn, visibly wrong.
 *
 * Clamps outside the cache's range in either mode, as `localPositionAt` does —
 * before the first sample yields the first orientation, after the last yields
 * the last. Always allocates, held sample included, so a caller can compose
 * into the result without rewriting the history it is reading.
 */
export function localRotationAt(
  cache: SampledRotations,
  time: bigint,
  interpolation: Interpolation,
): babylon.Quaternion | undefined {
  const bracket = sampleAt(cache.logtimes, time, interpolation);
  if (bracket === undefined) {
    return undefined;
  }
  const lastRotation = cache.allRotations.at(bracket.index);
  if (lastRotation === undefined) {
    return undefined;
  }
  const nextRotation = cache.allRotations.at(bracket.index + 1);
  if (bracket.fraction === undefined || nextRotation === undefined) {
    return lastRotation.clone();
  }
  return babylon.Quaternion.Slerp(lastRotation, nextRotation, bracket.fraction);
}

/**
 * Sets every entity's current orientation to the visualized time.
 *
 * The rotational counterpart of `updatePositions`, and run beside it, before the
 * hierarchy is composed — `resolveGlobalPositions` reads what this writes. Blends
 * or holds between samples as the panel's data settings say, read every frame
 * like `updatePositions` reads them.
 */
export function updateRotations(state: BabylonState) {
  const time = state.sceneConfig.time;
  if (time === undefined) {
    return;
  }
  const interpolation = interpolationOf(state);
  for (const entity of Object.values(state.entities)) {
    const rotation = entity.rotation;
    if (!rotation || rotation.cache.load.state !== "loaded") {
      continue;
    }
    const sampled = localRotationAt(rotation.cache, time, interpolation);
    if (!sampled) {
      continue;
    }
    // The visualized time, even for a held sample (unlike `Position.current.logtime`).
    rotation.current.logtime = time;
    rotation.current.quaternion = sampled;
  }
}
